// Mirror of backend App\Support\PassageText — book scans reach the student with
// running page numbers stranded mid-sentence, an orphaned dropped capital and the
// chapter/title block run into the first paragraph.

const WIN1252 = {
  "\u0080": "\u20AC", "\u0082": "\u201A", "\u0083": "\u0192", "\u0084": "\u201E",
  "\u0085": "\u2026", "\u0086": "\u2020", "\u0087": "\u2021", "\u0088": "\u02C6",
  "\u0089": "\u2030", "\u008A": "\u0160", "\u008B": "\u2039", "\u008C": "\u0152",
  "\u008E": "\u017D", "\u0091": "\u2018", "\u0092": "\u2019", "\u0093": "\u201C",
  "\u0094": "\u201D", "\u0095": "\u2022", "\u0096": "\u2013", "\u0097": "\u2014",
  "\u0098": "\u02DC", "\u0099": "\u2122", "\u009A": "\u0161", "\u009B": "\u203A",
  "\u009C": "\u0153", "\u009E": "\u017E", "\u009F": "\u0178",
};

const CHAPTER_WORDS = new Set([
  "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN",
  "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN",
  "EIGHTEEN", "NINETEEN", "TWENTY", "PROLOGUE", "EPILOGUE",
]);

const isPageNumberLine = (line) =>
  line !== "" && /^[[(\-–—|\s]*(?:page\s*)?\d{1,4}[\])\-–—|\s]*$/i.test(line);

export const isChapterLabel = (line) => {
  const normalized = (line || "").trim().toUpperCase();
  if (/^(CHAPTER|PART|BOOK|SECTION)\b[\s\-–—]*[IVXLCDM0-9]*\.?$/.test(normalized)) return true;
  if (/^[IVXLCDM]{1,7}\.?$/.test(normalized)) return true;
  if (/^\d{1,3}\.?$/.test(normalized)) return true;
  return CHAPTER_WORDS.has(normalized);
};

const looksLikeHeadingLine = (line, allowLowercase) => {
  if (line.length > 70 || line.split(/\s+/).filter(Boolean).length > 12) return false;
  if (/[.!?;:,"”’]$/u.test(line)) return false;
  if (/^[“"‘'(]/u.test(line)) return false;
  return isChapterLabel(line) || allowLowercase || /^[A-Z0-9]/u.test(line);
};

const reattachDropCap = (lines, letter) => {
  for (let i = 0; i < lines.length; i += 1) {
    // A short all-caps line is a heading, not the tail of a broken word.
    if (lines[i].length < 40 || !/[a-z]/.test(lines[i])) continue;
    if (/^[A-Z]{2,}\b/.test(lines[i])) {
      lines[i] = letter + lines[i];
      return lines;
    }
  }
  return lines;
};

export const cleanPassageText = (value) => {
  if (typeof value !== "string" || value === "") return "";

  let text = value.replace(/[\u0080-\u009F]/g, (c) => WIN1252[c] || "");
  text = text
    .replace(/\r\n?/g, "\n")
    .replace(/\u00A0/g, " ")
    .replace(/[\uFEFF\uFFFD]/g, "");

  const lines = text.split("\n").map((line) => line.replace(/[ \t]+/g, " ").trim());

  const kept = [];
  const orphanLetters = [];
  let skipping = false;
  lines.forEach((line) => {
    const dropCap = /^([A-Z])$/.exec(line);
    if (isPageNumberLine(line) || dropCap) {
      if (dropCap) orphanLetters.push(dropCap[1]);
      while (kept.length && kept[kept.length - 1] === "") kept.pop();
      skipping = kept.length > 0;
      return;
    }
    if (skipping) {
      if (line === "") return;
      skipping = false;
    }
    kept.push(line);
  });

  let result = kept;
  orphanLetters.forEach((letter) => {
    result = reattachDropCap(result, letter);
  });

  const out = [];
  let blankRun = 0;
  result.forEach((line) => {
    if (line === "") {
      blankRun += 1;
      if (blankRun > 1) return;
    } else {
      blankRun = 0;
    }
    out.push(line);
  });

  return out.join("\n").trim();
};

// Book scans are hard-wrapped at the scanner's column width, one physical line per \n,
// with no blank line between paragraphs — so a paragraph boundary can't be told apart
// from a mid-sentence wrap by looking at whitespace alone. Primary signal: a line that
// doesn't end its sentence continues on the next line. Also continue when a double
// quote is still open (wrap after "No, Meg.") or the next line starts lower-case.
const SENTENCE_END_RE = /[.!?…][)\]"'’”]*$/;

const hasOpenDoubleQuote = (s) => {
  let open = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === "\u201C") open = true;
    else if (ch === "\u201D") open = false;
    else if (ch === '"') open = !open;
  }
  return open;
};

const shouldMergeWithNext = (prev, next) => {
  if (!prev || prev === "") return false;
  if (!SENTENCE_END_RE.test(prev)) return true;
  if (hasOpenDoubleQuote(prev)) return true;
  if (/^[a-z]/.test(next)) return true;
  return false;
};

export const mergeWrappedLines = (text) => {
  if (typeof text !== "string" || text === "") return text;
  const lines = text.split("\n");
  const merged = [];
  lines.forEach((line) => {
    if (line === "") {
      merged.push(line);
      return;
    }
    const prev = merged.length ? merged[merged.length - 1] : null;
    if (shouldMergeWithNext(prev, line)) {
      merged[merged.length - 1] = `${prev} ${line}`;
    } else {
      merged.push(line);
    }
  });
  return merged.join("\n");
};

// True when sub-questions cite "line N" / "lines N–M" — exam papers need hard-wrap
// fidelity; book-scan worksheets do not.
export const passageQuestionsCiteLines = (questions = []) =>
  questions.some((q) => /\blines?\s+\d/i.test(q?.question_text || q?.question || q?.name || ""));

const nextNonEmptyLine = (lines, fromIndex) => {
  for (let i = fromIndex; i < lines.length; i += 1) {
    if (lines[i] !== "") return lines[i];
  }
  return null;
};

const wordCountLine = (line) => line.trim().split(/\s+/).filter(Boolean).length;

/**
 * When Quill / import glues the topic into the first sentence after a chapter label
 * ("Mrs Which In the forest evening…"), peel the short Title-Case topic off.
 */
export const peelJammedTopicFromBody = (body) => {
  const raw = typeof body === "string" ? body : "";
  if (!raw.trim()) return { title: null, body: raw };

  const lines = raw.split("\n");
  let firstIdx = 0;
  while (firstIdx < lines.length && lines[firstIdx].trim() === "") firstIdx += 1;
  if (firstIdx >= lines.length) return { title: null, body: raw };

  const line = lines[firstIdx].replace(/[ \t]+/g, " ").trim();
  // Non-greedy topic so "In"/"The" stay with the sentence, not the title.
  const m = line.match(
    /^((?:(?:Mrs?|Ms|Miss|Dr|Prof)\.?\s+)?[A-Z][A-Za-z0-9'’\-]*(?:\s+[A-Z][A-Za-z0-9'’\-]*){0,5}?)\s+((?:In|On|At|The|A|An|It|He|She|They|We|I|This|That|There|After|Before|When|As|Once|During|From|Toward|Towards|Across|Under|Over|Between|Among)\b.{15,})$/
  );
  if (!m) return { title: null, body: raw };

  const title = m[1].trim();
  const prose = m[2].trim();
  if (!title || !prose) return { title: null, body: raw };
  if (title.length > 48 || wordCountLine(title) > 6) return { title: null, body: raw };
  if (
    /^(?:Read|Study|Look|Answer|Write|Explain|Describe|Complete|The|A|An|In|On|At|To|For|By|From|With|As|Of|And|But|Or|So|If|When|While|After|Before|About|Once|Upon)$/i.test(
      title
    )
  ) {
    return { title: null, body: raw };
  }
  if (/\b(?:in|on|of|at|to|for|by|from|with|about|into|onto|upon|and|or|the|a|an)$/i.test(title)) {
    return { title: null, body: raw };
  }
  if (looksLikeHeadingLine(prose, true) && prose.length < 40) {
    return { title: null, body: raw };
  }

  const restLines = [...lines];
  restLines[firstIdx] = prose;
  return { title, body: restLines.join("\n").trim() };
};

// → { chapter, title, body }
export const structurePassage = (value) => {
  const body = cleanPassageText(value);
  if (!body) return { chapter: null, title: null, body: "" };

  const lines = body.split("\n");
  const headingLines = [];
  let index = 0;
  while (index < lines.length && headingLines.length < 4) {
    const line = lines[index];
    if (line === "") {
      if (headingLines.length === 0) {
        index += 1;
        continue;
      }
      // Blank lines between chapter and topic are common — only end the heading
      // block when the next non-empty line is no longer heading-like.
      const peek = nextNonEmptyLine(lines, index + 1);
      const allowLowercase = headingLines.length > 0 && isChapterLabel(headingLines[0]);
      if (peek && looksLikeHeadingLine(peek, allowLowercase)) {
        index += 1;
        continue;
      }
      break;
    }
    // A chapter label licenses the wrapped subtitle beneath it, which often
    // continues in lower case ("…takes place / which seems likely to…").
    const allowLowercase = headingLines.length > 0 && isChapterLabel(headingLines[0]);
    if (!looksLikeHeadingLine(line, allowLowercase)) break;
    headingLines.push(line);
    index += 1;
  }

  let rest = lines.slice(index).join("\n").trim();
  if (headingLines.length === 0 || rest === "") {
    return { chapter: null, title: null, body: mergeWrappedLines(body) };
  }

  const chapter = isChapterLabel(headingLines[0]) ? headingLines.shift() : null;
  let title = headingLines.length ? headingLines.join(" ") : null;
  if (!chapter && !title) return { chapter: null, title: null, body: mergeWrappedLines(body) };

  if (chapter && !title) {
    const peeled = peelJammedTopicFromBody(rest);
    if (peeled.title) {
      title = peeled.title;
      rest = peeled.body;
    }
  }

  return { chapter, title, body: mergeWrappedLines(rest) };
};
